import { useState, useEffect, useRef, useCallback } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { authorizedUser } from "src/helpers/Auth";
import { nuclearn_api_url } from "src/App";
import type { Artifact, EnrichedArtifact } from "../ArtifactSelectorModal";

interface UseArtifactsWithProgressiveSizesOptions {
  datasetId: number;
  datasetName: string;
  searchQuery?: string;
  enabled?: boolean;
}

interface ArtifactStreamState {
  artifacts: EnrichedArtifact[];
  artifactsMap: Map<number, EnrichedArtifact>;
  isLoading: boolean;
  isComplete: boolean;
  error: string | null;
  progress: number; // 0-100 percentage of sizes loaded
}

/**
 * Hook for streaming artifacts with progressive file size loading
 *
 * Uses Server-Sent Events (SSE) to:
 * 1. Immediately return all artifacts (with or without file sizes)
 * 2. Stream file size updates progressively as they're computed
 * 3. Signal completion when all sizes are computed
 *
 * @param options Configuration for the hook
 * @returns Current state of artifacts and loading progress
 */
export const useArtifactsWithProgressiveSizes = ({
  datasetId,
  datasetName,
  searchQuery = "",
  enabled = true,
}: UseArtifactsWithProgressiveSizesOptions): ArtifactStreamState => {
  const [state, setState] = useState<{
    artifacts: Map<number, EnrichedArtifact>;
    isLoading: boolean;
    isComplete: boolean;
    error: string | null;
    sizesLoaded: number;
    totalArtifacts: number;
  }>({
    artifacts: new Map(),
    isLoading: false,
    isComplete: false,
    error: null,
    sizesLoaded: 0,
    totalArtifacts: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchArtifacts = useCallback(async () => {
    if (!enabled) return;

    // Cleanup previous connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;

    setState((prev) => ({
      ...prev,
      isLoading: true,
      isComplete: false,
      error: null,
      artifacts: new Map(),
      sizesLoaded: 0,
      totalArtifacts: 0,
    }));

    const user = authorizedUser();

    // Handle relative URLs (e.g., '/api') by making them absolute
    const baseUrl = nuclearn_api_url?.startsWith("/")
      ? `${window.location.origin}${nuclearn_api_url}`
      : nuclearn_api_url;
    const url = new URL(
      `${baseUrl}/datasets/${datasetId}/artifacts/stream-with-sizes`,
    );

    if (searchQuery) {
      url.searchParams.append("filename", searchQuery);
    }

    try {
      await fetchEventSource(url.toString(), {
        method: "GET",
        signal: newAbortController.signal,
        headers: {
          ...(user.Authorization ? { Authorization: user.Authorization } : {}),
        },
        openWhenHidden: true,
        onopen: async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        },
        onmessage: (event) => {
          if (event.event === "artifacts") {
            // Phase 1: Receive initial artifact list
            const artifacts = JSON.parse(event.data) as Artifact[];
            const artifactsMap = new Map<number, EnrichedArtifact>();

            artifacts.forEach((artifact) => {
              artifactsMap.set(artifact.id, {
                ...artifact,
                dataset_id: datasetId,
                dataset_name: datasetName,
              });
            });

            setState((prev) => ({
              ...prev,
              artifacts: artifactsMap,
              totalArtifacts: artifacts.length,
              isLoading: false,
              // Count how many already have sizes (not null, not -1)
              sizesLoaded: artifacts.filter(
                (a) => a.filesize !== null && a.filesize !== -1,
              ).length,
            }));
          } else if (event.event === "file_size_update") {
            // Phase 2: Receive batched file size updates
            const updates = JSON.parse(event.data) as Array<{
              artifact_id: number;
              filesize: number;
            }>;

            setState((prev) => {
              const newArtifacts = new Map(prev.artifacts);
              let newSizesLoaded = prev.sizesLoaded;

              updates.forEach(({ artifact_id, filesize }) => {
                const artifact = newArtifacts.get(artifact_id);
                if (artifact) {
                  // Count this as loaded if it wasn't already
                  const wasLoading = artifact.filesize === null;
                  if (wasLoading) {
                    newSizesLoaded++;
                  }
                  newArtifacts.set(artifact_id, { ...artifact, filesize });
                }
              });

              return {
                ...prev,
                artifacts: newArtifacts,
                sizesLoaded: newSizesLoaded,
              };
            });
          } else if (event.event === "end") {
            // Phase 3: Stream complete
            setState((prev) => ({
              ...prev,
              isComplete: true,
              isLoading: false,
            }));
          }
        },
        onerror: (err) => {
          console.error("SSE connection error:", err);
          setState((prev) => ({
            ...prev,
            error: "Failed to load artifacts",
            isLoading: false,
            isComplete: true,
          }));
          throw err; // Stop retrying
        },
        onclose: () => {
          setState((prev) => ({
            ...prev,
            isComplete: true,
            isLoading: false,
          }));
        },
      });
    } catch (error: any) {
      // Only set error if it's not an abort (user closed modal or search changed)
      if (error.name !== "AbortError") {
        console.error("Error fetching artifacts:", error);
        setState((prev) => ({
          ...prev,
          error: error.message || "Failed to load artifacts",
          isLoading: false,
          isComplete: true,
        }));
      }
    }
  }, [datasetId, datasetName, searchQuery, enabled]);

  useEffect(() => {
    fetchArtifacts();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchArtifacts]);

  const progress =
    state.totalArtifacts > 0
      ? (state.sizesLoaded / state.totalArtifacts) * 100
      : 0;

  return {
    artifacts: Array.from(state.artifacts.values()),
    artifactsMap: state.artifacts,
    isLoading: state.isLoading,
    isComplete: state.isComplete,
    error: state.error,
    progress,
  };
};
